import { dark, light, pattern, type Palette } from "./tokens";

// The theme toggle has to move two sets of variables at once, because the app is
// mid-migration and both are live:
//
//   --c-*   the names tailwind.config.ts maps its colours onto, so every
//           `bg-pane-left` / `text-ink-muted` on a converted page follows.
//   legacy  --bg / --surface / --accent / ... , still read by the stylesheet we
//           deliberately kept (topology SVG, slide deck, terminal chrome,
//           markdown, course rail).
//
// Driving both from one palette is what makes the toggle actually switch the
// whole screen rather than just the parts that happen to be converted.

/** Tailwind-facing names. Keys must match the `themed()` calls in tailwind.config.ts. */
function cssVars(p: Palette, patterns: typeof pattern.dark): Record<string, string> {
  return {
    "--c-pane-left": p.paneLeft,
    "--c-pane-right": p.paneRight,
    "--c-card": p.card,
    "--c-editor-header": p.editorHeader,
    "--c-zone-fill": p.zoneFill,
    "--c-zone-border": p.zoneBorder,
    "--c-note": p.note,
    "--c-line": p.line,
    "--c-line-soft": p.lineSoft,
    "--c-ink-strong": p.inkStrong,
    "--c-ink": p.ink,
    "--c-ink-muted": p.inkMuted,
    "--c-ink-faint": p.inkFaint,
    "--c-ok": p.ok,
    "--c-warn": p.warn,
    "--c-danger": p.danger,
    "--c-orange": p.orange,
    "--c-accent": p.accent,
    "--c-accent-hover": p.accentHover,
    "--c-on-accent": p.onAccent,
    "--c-tech-grid": patterns.techGrid,
    "--c-tech-dots": patterns.techDots,
    "--c-tech-dots-dark": patterns.techDotsDark,

    // Legacy names, still consumed by the stylesheet we kept on purpose.
    "--bg": p.paneRight,
    "--surface": p.paneLeft,
    "--surface-2": p.card,
    "--surface-3": p.editorHeader,
    "--text": p.ink,
    "--muted": p.inkMuted,
    "--line": p.line,
    "--accent": p.accent,
    "--accent-soft": p.zoneFill,
    "--accent-text": p.onAccent,
    "--ok": p.ok,
    "--warn": p.warn,
    "--danger": p.danger,
    "--orange": p.orange,
    "--focus": `0 0 0 3px ${p.accent}55`,
    "--thumb": p.thumb,
    "--thumb-hover": p.thumbHover,
  };
}

export const paletteFor = (theme: "light" | "dark"): Palette => (theme === "dark" ? dark : light);

/** Write a theme's variables onto :root. Called at startup and on every toggle. */
export function applyPalette(theme: "light" | "dark"): void {
  const root = document.documentElement;
  const vars = cssVars(paletteFor(theme), theme === "dark" ? pattern.dark : pattern.light);
  for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value);
  // `color-scheme` is what gets form controls, scrollbars and the canvas the
  // browser paints outside our elements to match — without it a light theme
  // still shows dark native scrollbars.
  root.style.colorScheme = theme;
}

/** Exported for the test that guards styles.css's pre-hydration fallback. */
export const varsForTest = (theme: "light" | "dark") =>
  cssVars(paletteFor(theme), theme === "dark" ? pattern.dark : pattern.light);
